<script>
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';

export default {
  name: 'SLAOperationsSummary',
  components: {
    NextButton,
    Spinner,
  },
  props: {
    summary: {
      type: Object,
      default: null,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    activeWindowHours: {
      type: Number,
      default: 24,
    },
    isProcessingAction: {
      type: Boolean,
      default: false,
    },
    processingActionId: {
      type: Number,
      default: null,
    },
  },
  emits: ['refresh', 'windowChange', 'acknowledge', 'recover'],
  data() {
    return {
      activeMetric: null,
      activeDimensionFilter: 'all',
      actionModal: {
        action: null,
        breach: null,
        reason: '',
      },
    };
  },
  computed: {
    windowOptions() {
      return [
        {
          value: 24,
          label: this.$t('SLA_REPORTS.OPERATIONS.WINDOWS.24'),
        },
        {
          value: 72,
          label: this.$t('SLA_REPORTS.OPERATIONS.WINDOWS.72'),
        },
        {
          value: 168,
          label: this.$t('SLA_REPORTS.OPERATIONS.WINDOWS.168'),
        },
      ];
    },
    generatedAtLabel() {
      return this.formatDate(this.summary?.generated_at);
    },
    metricGroups() {
      return [
        {
          key: 'timers',
          title: this.$t('SLA_REPORTS.OPERATIONS.GROUPS.TIMERS'),
          items: [
            this.metricItem(
              this.$t('SLA_REPORTS.OPERATIONS.METRICS.ACTIVE_TIMERS'),
              'timers',
              'active',
              'info'
            ),
            this.metricItem(
              this.$t('SLA_REPORTS.OPERATIONS.METRICS.DUE_SOON'),
              'timers',
              'due_soon',
              'warning'
            ),
            this.metricItem(
              this.$t('SLA_REPORTS.OPERATIONS.METRICS.PAUSED_TIMERS'),
              'timers',
              'paused',
              'warning'
            ),
            this.metricItem(
              this.$t('SLA_REPORTS.OPERATIONS.METRICS.BREACHED_TIMERS'),
              'timers',
              'breached',
              'danger'
            ),
            this.metricItem(
              this.$t('SLA_REPORTS.OPERATIONS.METRICS.ACKNOWLEDGED_TIMERS'),
              'timers',
              'acknowledged',
              'success'
            ),
            this.metricItem(
              this.$t('SLA_REPORTS.OPERATIONS.METRICS.RECOVERED_TIMERS'),
              'timers',
              'resolved_in_window',
              'success'
            ),
          ],
        },
        {
          key: 'breaches',
          title: this.$t('SLA_REPORTS.OPERATIONS.GROUPS.BREACHES'),
          items: [
            this.metricItem(
              this.$t('SLA_REPORTS.OPERATIONS.METRICS.ACTIVE_BREACHES'),
              'breaches',
              'active',
              'danger'
            ),
            this.metricItem(
              this.$t('SLA_REPORTS.OPERATIONS.METRICS.OPEN_BREACHES'),
              'breaches',
              'open',
              'warning'
            ),
            this.metricItem(
              this.$t('SLA_REPORTS.OPERATIONS.METRICS.RECOVERED_IN_WINDOW'),
              'breaches',
              'recovered_in_window',
              'success'
            ),
            this.metricItem(
              this.$t('SLA_REPORTS.OPERATIONS.METRICS.ACKNOWLEDGED_BREACHES'),
              'breaches',
              'acknowledged',
              'success'
            ),
          ],
        },
        {
          key: 'notifications',
          title: this.$t('SLA_REPORTS.OPERATIONS.GROUPS.NOTIFICATIONS'),
          items: [
            this.metricItem(
              this.$t('SLA_REPORTS.OPERATIONS.METRICS.DISPATCHED'),
              'notifications',
              'dispatched',
              'success'
            ),
            this.metricItem(
              this.$t('SLA_REPORTS.OPERATIONS.METRICS.SUPPRESSED'),
              'notifications',
              'suppressed',
              'warning'
            ),
            this.metricItem(
              this.$t('SLA_REPORTS.OPERATIONS.METRICS.PENDING'),
              'notifications',
              'pending',
              'danger'
            ),
          ],
        },
        {
          key: 'policies',
          title: this.$t('SLA_REPORTS.OPERATIONS.GROUPS.POLICIES'),
          items: [
            this.metricItem(
              this.$t('SLA_REPORTS.OPERATIONS.METRICS.ACTIVE_POLICIES'),
              'policies',
              'active_policy_versions',
              'info'
            ),
            this.metricItem(
              this.$t('SLA_REPORTS.OPERATIONS.METRICS.UNSUPPORTED_STEPS'),
              'policies',
              'unsupported_escalation_steps',
              'danger'
            ),
          ],
        },
      ];
    },
    activeBreaches() {
      return this.summary?.active_breaches || [];
    },
    recoveredBreaches() {
      return this.summary?.recovered_breaches || [];
    },
    timers() {
      return [
        ...(this.summary?.timers || []),
        ...(this.summary?.timers_at_risk || []),
      ].reduce((records, timer) => {
        if (!records.some(record => record.id === timer.id))
          records.push(timer);
        return records;
      }, []);
    },
    timersAtRisk() {
      return this.summary?.timers_at_risk || [];
    },
    unsupportedSteps() {
      return this.summary?.escalation?.unsupported_steps || [];
    },
    hasSummary() {
      return Boolean(this.summary);
    },
    isActionModalOpen() {
      return Boolean(this.actionModal.action && this.actionModal.breach);
    },
    permissions() {
      return this.summary?.permissions || {};
    },
    canAcknowledgeBreaches() {
      return Boolean(this.permissions.can_acknowledge_breach);
    },
    canRecoverBreaches() {
      return Boolean(this.permissions.can_recover_breach);
    },
    drilldownMetric() {
      return this.activeMetric;
    },
    hasDrilldownOpen() {
      return Boolean(this.activeMetric);
    },
    drilldownTitle() {
      if (!this.drilldownMetric) return '';

      return this.$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.TITLE', {
        metric: this.drilldownMetric.label,
      });
    },
    rawDrilldownRecords() {
      if (!this.drilldownMetric) return [];

      return this.recordsForMetric(this.drilldownMetric);
    },
    drilldownDimensionOptions() {
      const options = new Map([
        [
          'all',
          {
            id: 'all',
            label: this.$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.FILTER_ALL'),
          },
        ],
      ]);

      this.rawDrilldownRecords.forEach(record => {
        this.dimensionEntriesForRecord(record).forEach(option => {
          if (!options.has(option.id)) options.set(option.id, option);
        });
      });

      return Array.from(options.values());
    },
    hasDimensionFilters() {
      return this.drilldownDimensionOptions.length > 1;
    },
    drilldownRecords() {
      if (this.activeDimensionFilter === 'all') return this.rawDrilldownRecords;

      return this.rawDrilldownRecords.filter(record =>
        this.dimensionEntriesForRecord(record).some(
          option => option.id === this.activeDimensionFilter
        )
      );
    },
    drilldownCountLabel() {
      return this.$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.COUNT', {
        count: this.drilldownRecords.length,
      });
    },
    actionReason() {
      return this.actionModal.reason.trim();
    },
    isActionReasonValid() {
      return this.actionReason.length >= 12;
    },
    actionModalTitle() {
      if (this.actionModal.action === 'recover') {
        return this.$t('SLA_REPORTS.OPERATIONS.ACTIONS.RECOVER_TITLE');
      }

      return this.$t('SLA_REPORTS.OPERATIONS.ACTIONS.ACKNOWLEDGE_TITLE');
    },
    actionModalConfirmLabel() {
      if (this.actionModal.action === 'recover') {
        return this.$t('SLA_REPORTS.OPERATIONS.ACTIONS.RECOVER');
      }

      return this.$t('SLA_REPORTS.OPERATIONS.ACTIONS.ACKNOWLEDGE');
    },
    actionModalIcon() {
      if (this.actionModal.action === 'recover') {
        return 'i-ph-check-circle';
      }

      return 'i-ph-hand-palm';
    },
  },
  methods: {
    metricItem(label, group, key, tone) {
      return {
        id: `${group}:${key}`,
        group,
        key,
        label,
        value: this.formatNumber(this.summary?.metrics?.[group]?.[key]),
        tone,
      };
    },
    selectMetric(metric) {
      this.activeMetric = metric;
      this.activeDimensionFilter = 'all';
    },
    closeDrilldown() {
      this.activeMetric = null;
      this.activeDimensionFilter = 'all';
    },
    isMetricSelected(metric) {
      return this.drilldownMetric?.id === metric.id;
    },
    recordsForMetric(metric) {
      if (!metric) return [];

      if (metric.group === 'timers') {
        return this.timerRecordsForMetric(metric.key);
      }

      if (metric.group === 'breaches') {
        return this.breachRecordsForMetric(metric.key);
      }

      if (metric.group === 'notifications') {
        return this.notificationRecordsForMetric(metric.key);
      }

      if (
        metric.group === 'policies' &&
        metric.key === 'unsupported_escalation_steps'
      ) {
        return this.unsupportedSteps.map(step =>
          this.stepDrilldownRecord(step)
        );
      }

      return [];
    },
    timerRecordsForMetric(key) {
      return this.timers
        .filter(timer => this.timerMatchesMetric(timer, key))
        .map(timer => this.timerDrilldownRecord(timer));
    },
    breachRecordsForMetric(key) {
      return [...this.activeBreaches, ...this.recoveredBreaches]
        .filter(breach => this.breachMatchesMetric(breach, key))
        .map(breach => this.breachDrilldownRecord(breach));
    },
    notificationRecordsForMetric(key) {
      return [...this.activeBreaches, ...this.recoveredBreaches]
        .filter(breach => this.notificationMatchesMetric(breach, key))
        .map(breach => this.breachDrilldownRecord(breach));
    },
    timerMatchesMetric(timer, key) {
      const state = timer.operational_state || timer.status;

      switch (key) {
        case 'active':
          return ['running', 'paused', 'breached', 'acknowledged'].includes(
            timer.status
          );
        case 'due_soon':
          return state === 'at_risk';
        case 'paused':
          return state === 'paused';
        case 'breached':
          return state === 'violated';
        case 'acknowledged':
          return timer.status === 'acknowledged';
        case 'resolved_in_window':
          return state === 'recovered';
        default:
          return false;
      }
    },
    breachMatchesMetric(breach, key) {
      switch (key) {
        case 'active':
          return ['open', 'acknowledged'].includes(breach.status);
        case 'open':
          return breach.status === 'open';
        case 'acknowledged':
          return breach.status === 'acknowledged';
        case 'recovered_in_window':
          return breach.status === 'recovered';
        default:
          return false;
      }
    },
    notificationMatchesMetric(breach, key) {
      const status = breach.notification_delivery?.status;

      if (key === 'pending') {
        return !['dispatched', 'suppressed'].includes(status);
      }

      return status === key;
    },
    timerDrilldownRecord(timer) {
      return {
        id: `timer-${timer.id}`,
        type: 'timer',
        title: this.$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.TIMER_TITLE', {
          id: timer.id,
        }),
        timer,
        source: timer.source,
        policy: timer.policy,
        owner: timer.owner,
        state: timer.operational_state || timer.status,
        timeline: timer.timeline || [],
      };
    },
    breachDrilldownRecord(breach) {
      return {
        id: `breach-${breach.id}`,
        type: 'breach',
        title: this.$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.BREACH_TITLE', {
          id: breach.id,
        }),
        breach,
        timer: breach.timer,
        source: breach.source || breach.timer?.source,
        policy: breach.policy || breach.timer?.policy,
        owner: breach.owner,
        currentOwner: breach.current_owner,
        state: breach.operational_state || breach.status,
        timeline: breach.timeline || breach.timer?.timeline || [],
      };
    },
    stepDrilldownRecord(step) {
      return {
        id: `step-${step.id}`,
        type: 'step',
        title: this.$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.STEP_TITLE', {
          id: step.id,
        }),
        step,
        policy: { id: step.policy_version_id },
        owner: step.owner,
        state: 'unsupported',
        timeline: [],
      };
    },
    dimensionEntriesForRecord(record) {
      const timer = record.timer || record.breach?.timer;
      const source = record.source;
      const entries = [
        this.dimensionEntry('timer', timer?.id, this.idLabel(timer?.id)),
        this.dimensionEntry('source', source?.id, this.sourceLabel(source)),
        this.dimensionEntry(
          'policy',
          record.policy?.id,
          this.policyLabel(record.policy)
        ),
        this.dimensionEntry(
          'inbox',
          source?.inbox?.id,
          this.inboxLabel(source)
        ),
        this.dimensionEntry('team', source?.team?.id, this.teamLabel(source)),
        this.dimensionEntry(
          'conversation',
          source?.conversation?.display_id || source?.conversation?.id,
          this.conversationLabel(source)
        ),
      ];

      return entries.filter(Boolean);
    },
    dimensionEntry(type, value, valueLabel) {
      if (
        !value ||
        !valueLabel ||
        valueLabel === this.$t('SLA_REPORTS.OPERATIONS.NOT_AVAILABLE')
      ) {
        return null;
      }

      return {
        id: `${type}:${value}`,
        label: `${this.dimensionTypeLabel(type)} ${valueLabel}`,
      };
    },
    dimensionTypeLabel(type) {
      switch (type) {
        case 'timer':
          return this.$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.DIMENSIONS.TIMER');
        case 'source':
          return this.$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.DIMENSIONS.SOURCE');
        case 'policy':
          return this.$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.DIMENSIONS.RULE');
        case 'inbox':
          return this.$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.DIMENSIONS.INBOX');
        case 'team':
          return this.$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.DIMENSIONS.TEAM');
        case 'conversation':
          return this.$t(
            'SLA_REPORTS.OPERATIONS.DRILLDOWN.DIMENSIONS.CONVERSATION'
          );
        default:
          return type;
      }
    },
    recordTypeLabel(record) {
      switch (record.type) {
        case 'breach':
          return this.$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.TYPES.BREACH');
        case 'step':
          return this.$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.TYPES.STEP');
        case 'timer':
        default:
          return this.$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.TYPES.TIMER');
      }
    },
    stateLabel(state) {
      switch (state) {
        case 'running':
          return this.$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.STATES.RUNNING');
        case 'at_risk':
          return this.$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.STATES.AT_RISK');
        case 'paused':
          return this.$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.STATES.PAUSED');
        case 'violated':
          return this.$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.STATES.VIOLATED');
        case 'recovered':
          return this.$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.STATES.RECOVERED');
        case 'acknowledged':
          return this.$t(
            'SLA_REPORTS.OPERATIONS.DRILLDOWN.STATES.ACKNOWLEDGED'
          );
        case 'unsupported':
          return this.$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.STATES.UNSUPPORTED');
        case 'missing_data':
        default:
          return this.$t(
            'SLA_REPORTS.OPERATIONS.DRILLDOWN.STATES.MISSING_DATA'
          );
      }
    },
    policyLabel(policy) {
      if (!policy) return this.$t('SLA_REPORTS.OPERATIONS.NOT_AVAILABLE');

      const version = policy.version_number ? ` v${policy.version_number}` : '';
      return `${policy.name || this.idLabel(policy.id)}${version}`;
    },
    inboxLabel(source) {
      const inbox = source?.inbox;
      if (!inbox) return this.$t('SLA_REPORTS.OPERATIONS.NOT_AVAILABLE');

      return inbox.name || this.idLabel(inbox.id);
    },
    teamLabel(source) {
      const team = source?.team;
      if (!team) return this.$t('SLA_REPORTS.OPERATIONS.NOT_AVAILABLE');

      return team.name || this.idLabel(team.id);
    },
    conversationLabel(source) {
      const conversation = source?.conversation;
      if (!conversation) {
        return this.$t('SLA_REPORTS.OPERATIONS.NOT_AVAILABLE');
      }

      return this.idLabel(conversation.display_id || conversation.id);
    },
    recordCauseLabel(record) {
      const timer = record.timer || record.breach?.timer;
      const reason = this.deadlineReasonLabel(timer);

      if (reason) return reason;
      if (record.breach?.escalation_step?.name) {
        return record.breach.escalation_step.name;
      }

      return this.$t('SLA_REPORTS.OPERATIONS.NOT_AVAILABLE');
    },
    timelineLabel(event) {
      const actor = this.ownerLabel(event.actor);
      return this.$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.TIMELINE_ITEM', {
        event: event.event_type,
        time: this.formatDate(event.occurred_at),
        owner: actor,
      });
    },
    pauseHistoryRows(record) {
      return (record.timeline || [])
        .filter(event => ['paused', 'resumed'].includes(event.event_type))
        .map(event => ({
          id: event.id,
          label:
            event.event_type === 'paused'
              ? this.$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.PAUSED_EVENT')
              : this.$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.RESUMED_EVENT'),
          occurredAt: this.formatDate(event.occurred_at),
          actor: this.ownerLabel(event.actor),
          detail: this.pauseEventDetail(event),
        }));
    },
    pauseEventDetail(event) {
      const metadata = event.metadata || {};
      const details = [];
      if (metadata.pause_interval_id) {
        details.push(
          this.$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.PAUSE_INTERVAL', {
            id: metadata.pause_interval_id,
          })
        );
      }
      if (metadata.effective_pause_duration_seconds) {
        details.push(
          this.$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.EFFECTIVE_PAUSE', {
            minutes: this.secondsToMinutes(
              metadata.effective_pause_duration_seconds
            ),
          })
        );
      }

      return details.join(' · ');
    },
    pauseDiagnostics(record) {
      const timer = record.timer || record.breach?.timer;
      const diagnosis = timer?.deadline_drift_diagnosis;
      if (!diagnosis) return [];

      return [
        this.pauseDiagnosisLine('RAW_PAUSE', diagnosis.raw_pause_seconds),
        this.pauseDiagnosisLine(
          'EFFECTIVE_PAUSE',
          diagnosis.effective_business_pause_seconds
        ),
        this.pauseDiagnosisLine('PAUSE_DRIFT', diagnosis.drift_seconds),
      ].filter(Boolean);
    },
    pauseDiagnosisLine(key, seconds) {
      if (!Number.isFinite(Number(seconds))) return null;
      const minutes = this.secondsToMinutes(seconds);

      if (key === 'EFFECTIVE_PAUSE') {
        return this.$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.EFFECTIVE_PAUSE', {
          minutes,
        });
      }

      if (key === 'PAUSE_DRIFT') {
        return this.$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.PAUSE_DRIFT', {
          minutes,
        });
      }

      return this.$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.RAW_PAUSE', {
        minutes,
      });
    },
    metricToneClass(tone) {
      const classes = {
        danger: 'border-n-ruby-6 bg-n-ruby-2 text-n-ruby-11',
        warning: 'border-n-amber-6 bg-n-amber-2 text-n-amber-11',
        success: 'border-n-teal-6 bg-n-teal-2 text-n-teal-11',
        info: 'border-n-blue-6 bg-n-blue-2 text-n-blue-11',
      };

      return classes[tone] || 'border-n-container bg-n-slate-1 text-n-slate-12';
    },
    formatNumber(value) {
      const numericValue = Number(value || 0);

      if (!Number.isFinite(numericValue)) return value;

      return new Intl.NumberFormat().format(numericValue);
    },
    formatDate(value) {
      if (!value) return this.$t('SLA_REPORTS.OPERATIONS.NOT_AVAILABLE');

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;

      return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(date);
    },
    formatDelay(minutes) {
      const numericValue = Number(minutes);
      if (!Number.isFinite(numericValue)) {
        return this.$t('SLA_REPORTS.OPERATIONS.NOT_AVAILABLE');
      }

      return this.$t('SLA_REPORTS.OPERATIONS.MINUTES', {
        count: numericValue,
      });
    },
    ownerLabel(owner) {
      if (!owner) return this.$t('SLA_REPORTS.OPERATIONS.NO_OWNER');

      return owner.name || `${owner.type || 'Owner'} #${owner.id}`;
    },
    currentOwnerLabel(currentOwner) {
      return this.ownerLabel(currentOwner?.owner);
    },
    sourceLabel(source) {
      if (!source) return this.$t('SLA_REPORTS.OPERATIONS.NOT_AVAILABLE');

      const sourceType = source.type || 'Source';
      const sourceId = source.display_id || source.id;
      return sourceId ? `${sourceType} #${sourceId}` : sourceType;
    },
    channelsLabel(channels) {
      const channelList = Array.isArray(channels) ? channels : [];

      if (!channelList.length) {
        return this.$t('SLA_REPORTS.OPERATIONS.NOT_AVAILABLE');
      }

      return channelList.join(', ');
    },
    notificationLabel(notificationDelivery) {
      return (
        notificationDelivery?.status ||
        this.$t('SLA_REPORTS.OPERATIONS.NOT_AVAILABLE')
      );
    },
    deadlineExplanation(timer) {
      return timer?.deadline_explanation || {};
    },
    deadlineStatusLabel(timer) {
      const status = this.deadlineExplanation(timer).status || 'missing_data';
      switch (status) {
        case 'paused':
          return this.$t('SLA_REPORTS.OPERATIONS.DEADLINE.STATUS.PAUSED');
        case 'overdue':
          return this.$t('SLA_REPORTS.OPERATIONS.DEADLINE.STATUS.OVERDUE');
        case 'due_soon':
          return this.$t('SLA_REPORTS.OPERATIONS.DEADLINE.STATUS.DUE_SOON');
        case 'scheduled':
          return this.$t('SLA_REPORTS.OPERATIONS.DEADLINE.STATUS.SCHEDULED');
        case 'missing_data':
        default:
          return this.$t('SLA_REPORTS.OPERATIONS.DEADLINE.STATUS.MISSING_DATA');
      }
    },
    deadlineCalendarLabel(timer) {
      const calendar = this.deadlineExplanation(timer).calendar;
      if (!calendar) return this.$t('SLA_REPORTS.OPERATIONS.NOT_AVAILABLE');

      return this.$t('SLA_REPORTS.OPERATIONS.DEADLINE.CALENDAR_SUMMARY', {
        mode: this.deadlineModeLabel(calendar.mode),
        zone: calendar.time_zone,
      });
    },
    deadlineModeLabel(mode) {
      const normalizedMode = mode || 'calendar_missing';
      switch (normalizedMode) {
        case 'continuous_calendar':
          return this.$t(
            'SLA_REPORTS.OPERATIONS.DEADLINE.MODES.CONTINUOUS_CALENDAR'
          );
        case 'business_hours_calendar':
          return this.$t(
            'SLA_REPORTS.OPERATIONS.DEADLINE.MODES.BUSINESS_HOURS_CALENDAR'
          );
        case 'calendar_missing':
        default:
          return this.$t(
            'SLA_REPORTS.OPERATIONS.DEADLINE.MODES.CALENDAR_MISSING'
          );
      }
    },
    deadlineLocalDueLabel(timer) {
      const dueAt = this.deadlineExplanation(timer).deadline?.due_at_local;
      if (!dueAt) return null;

      return this.$t('SLA_REPORTS.OPERATIONS.DEADLINE.LOCAL_DUE', {
        time: this.formatDate(dueAt),
      });
    },
    deadlinePauseLabel(timer) {
      const pauses = this.deadlineExplanation(timer).pauses;
      if (!pauses) return null;

      if (pauses.active) {
        return this.$t('SLA_REPORTS.OPERATIONS.DEADLINE.ACTIVE_PAUSE', {
          reason:
            pauses.active_pause?.reason ||
            this.$t('SLA_REPORTS.OPERATIONS.NOT_AVAILABLE'),
          minutes: this.secondsToMinutes(pauses.active_elapsed_seconds),
        });
      }

      if (pauses.count > 0) {
        return this.$t('SLA_REPORTS.OPERATIONS.DEADLINE.PAUSE_HISTORY', {
          count: pauses.count,
          minutes: this.secondsToMinutes(pauses.total_closed_seconds),
        });
      }

      return this.$t('SLA_REPORTS.OPERATIONS.DEADLINE.NO_PAUSES');
    },
    deadlineHolidayLabel(timer) {
      const holidays =
        this.deadlineExplanation(timer).calendar?.holidays_in_window;
      if (!Array.isArray(holidays) || holidays.length === 0) return null;

      return this.$t('SLA_REPORTS.OPERATIONS.DEADLINE.HOLIDAYS', {
        dates: holidays.join(', '),
      });
    },
    deadlineClosedWeekdaysLabel(timer) {
      const closedWeekdays =
        this.deadlineExplanation(timer).calendar?.closed_weekdays;
      if (!Array.isArray(closedWeekdays) || closedWeekdays.length === 0) {
        return null;
      }

      return this.$t('SLA_REPORTS.OPERATIONS.DEADLINE.CLOSED_WEEKDAYS', {
        weekdays: closedWeekdays.join(', '),
      });
    },
    deadlineReasonLabel(timer) {
      const reasonCodes = this.deadlineExplanation(timer).reason_codes;
      if (!Array.isArray(reasonCodes) || reasonCodes.length === 0) return null;

      return reasonCodes
        .slice(0, 4)
        .map(code => this.deadlineReasonCodeLabel(code))
        .join(', ');
    },
    deadlineReasonCodeLabel(code) {
      switch (code) {
        case 'calendar_missing':
          return this.$t(
            'SLA_REPORTS.OPERATIONS.DEADLINE.REASON_CODES.CALENDAR_MISSING'
          );
        case 'continuous_calendar':
          return this.$t(
            'SLA_REPORTS.OPERATIONS.DEADLINE.REASON_CODES.CONTINUOUS_CALENDAR'
          );
        case 'business_hours_calendar':
          return this.$t(
            'SLA_REPORTS.OPERATIONS.DEADLINE.REASON_CODES.BUSINESS_HOURS_CALENDAR'
          );
        case 'holiday_calendar_configured':
          return this.$t(
            'SLA_REPORTS.OPERATIONS.DEADLINE.REASON_CODES.HOLIDAY_CALENDAR_CONFIGURED'
          );
        case 'holiday_in_deadline_window':
          return this.$t(
            'SLA_REPORTS.OPERATIONS.DEADLINE.REASON_CODES.HOLIDAY_IN_DEADLINE_WINDOW'
          );
        case 'dst_boundary_crossed':
          return this.$t(
            'SLA_REPORTS.OPERATIONS.DEADLINE.REASON_CODES.DST_BOUNDARY_CROSSED'
          );
        case 'active_pause_open':
          return this.$t(
            'SLA_REPORTS.OPERATIONS.DEADLINE.REASON_CODES.ACTIVE_PAUSE_OPEN'
          );
        case 'pause_history_present':
          return this.$t(
            'SLA_REPORTS.OPERATIONS.DEADLINE.REASON_CODES.PAUSE_HISTORY_PRESENT'
          );
        case 'no_pause_history':
          return this.$t(
            'SLA_REPORTS.OPERATIONS.DEADLINE.REASON_CODES.NO_PAUSE_HISTORY'
          );
        case 'due_soon_threshold_elapsed':
          return this.$t(
            'SLA_REPORTS.OPERATIONS.DEADLINE.REASON_CODES.DUE_SOON_THRESHOLD_ELAPSED'
          );
        case 'deadline_elapsed':
          return this.$t(
            'SLA_REPORTS.OPERATIONS.DEADLINE.REASON_CODES.DEADLINE_ELAPSED'
          );
        case 'deadline_data_missing':
          return this.$t(
            'SLA_REPORTS.OPERATIONS.DEADLINE.REASON_CODES.DEADLINE_DATA_MISSING'
          );
        default:
          return code;
      }
    },
    secondsToMinutes(seconds) {
      const numericValue = Number(seconds || 0);
      if (!Number.isFinite(numericValue)) return 0;

      return Math.max(Math.round(numericValue / 60), 0);
    },
    idLabel(id) {
      return id ? `#${id}` : this.$t('SLA_REPORTS.OPERATIONS.NOT_AVAILABLE');
    },
    isBreachProcessing(breach) {
      return this.isProcessingAction && this.processingActionId === breach.id;
    },
    canAcknowledge(breach) {
      return breach.status === 'open';
    },
    canRecover(breach) {
      return ['open', 'acknowledged'].includes(breach.status);
    },
    isBreachActionDisabled(breach, action) {
      if (this.isProcessingAction) return true;
      if (action === 'acknowledge') {
        return !this.canAcknowledgeBreaches || !this.canAcknowledge(breach);
      }

      return !this.canRecoverBreaches || !this.canRecover(breach);
    },
    openActionModal(action, breach) {
      if (this.isBreachActionDisabled(breach, action)) return;

      this.actionModal = {
        action,
        breach,
        reason: '',
      };
    },
    closeActionModal() {
      if (this.isProcessingAction) return;

      this.actionModal = {
        action: null,
        breach: null,
        reason: '',
      };
    },
    submitActionModal() {
      if (!this.isActionReasonValid || !this.actionModal.breach) return;

      const payload = {
        id: this.actionModal.breach.id,
        reason: this.actionReason,
      };
      if (this.actionModal.action === 'recover') {
        this.closeActionModal();
        this.$emit('recover', payload);
        return;
      }
      this.closeActionModal();
      this.$emit('acknowledge', payload);
    },
    onWindowChange(windowHours) {
      if (windowHours === this.activeWindowHours) return;

      this.$emit('windowChange', windowHours);
    },
  },
};
</script>

<template>
  <section
    class="min-w-full shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 p-6"
    data-testid="sla-operations-summary"
  >
    <div
      class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between"
    >
      <div class="min-w-0">
        <h2 class="text-base font-semibold text-n-slate-12">
          {{ $t('SLA_REPORTS.OPERATIONS.TITLE') }}
        </h2>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ $t('SLA_REPORTS.OPERATIONS.SUBTITLE') }}
        </p>
        <p v-if="hasSummary" class="mt-2 text-xs text-n-slate-10">
          {{ $t('SLA_REPORTS.OPERATIONS.GENERATED_AT') }}
          {{ generatedAtLabel }}
        </p>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <div
          class="inline-grid grid-flow-col auto-cols-fr overflow-hidden rounded-lg outline outline-1 outline-n-container"
          role="group"
          :aria-label="$t('SLA_REPORTS.OPERATIONS.WINDOW_LABEL')"
        >
          <button
            v-for="option in windowOptions"
            :key="option.value"
            type="button"
            class="h-8 min-w-14 px-3 text-sm font-medium transition-colors"
            :class="
              option.value === activeWindowHours
                ? 'bg-n-brand text-white'
                : 'bg-n-solid-1 text-n-slate-11 hover:bg-n-alpha-2'
            "
            @click="onWindowChange(option.value)"
          >
            {{ option.label }}
          </button>
        </div>
        <NextButton
          icon="i-ph-arrow-clockwise"
          color="slate"
          variant="outline"
          size="sm"
          :is-loading="isLoading"
          :aria-label="$t('SLA_REPORTS.OPERATIONS.REFRESH')"
          :title="$t('SLA_REPORTS.OPERATIONS.REFRESH')"
          data-testid="sla-operations-refresh"
          @click="$emit('refresh')"
        />
      </div>
    </div>

    <div
      v-if="isLoading && !hasSummary"
      class="flex items-center justify-center h-32 gap-2"
    >
      <Spinner />
      <span>{{ $t('SLA_REPORTS.OPERATIONS.LOADING') }}</span>
    </div>

    <div
      v-else-if="hasError"
      class="flex items-center justify-center h-32 text-sm text-n-ruby-11"
      data-testid="sla-operations-error"
    >
      {{ $t('SLA_REPORTS.OPERATIONS.ERROR') }}
    </div>

    <div
      v-else-if="!hasSummary"
      class="flex items-center justify-center h-32 text-sm text-n-slate-11"
    >
      {{ $t('SLA_REPORTS.OPERATIONS.EMPTY') }}
    </div>

    <div v-else class="mt-6 flex flex-col gap-6">
      <div class="grid grid-cols-1 gap-4 xl:grid-cols-4 md:grid-cols-2">
        <div
          v-for="group in metricGroups"
          :key="group.key"
          class="rounded-lg border border-n-container bg-n-solid-1 p-4"
        >
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ group.title }}
          </h3>
          <div class="mt-4 grid grid-cols-2 gap-3">
            <button
              v-for="metric in group.items"
              :key="metric.id"
              type="button"
              class="min-h-20 rounded-md border p-3 text-left transition-all hover:-translate-y-0.5 hover:shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
              :class="[
                metricToneClass(metric.tone),
                isMetricSelected(metric) ? 'ring-2 ring-n-brand' : '',
              ]"
              :aria-pressed="isMetricSelected(metric)"
              :data-testid="`sla-metric-${metric.group}-${metric.key}`"
              @click="selectMetric(metric)"
            >
              <span class="block text-xs font-medium text-n-slate-11">
                {{ metric.label }}
              </span>
              <span class="mt-2 block text-2xl font-semibold leading-none">
                {{ metric.value }}
              </span>
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="hasDrilldownOpen"
        class="rounded-lg border border-n-container bg-n-solid-1"
        data-testid="sla-operations-drilldown"
      >
        <div
          class="flex flex-col gap-3 border-b border-n-container px-4 py-3 md:flex-row md:items-center md:justify-between"
        >
          <div class="min-w-0">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ drilldownTitle }}
            </h3>
            <p class="mt-1 text-xs text-n-slate-10">
              {{ drilldownCountLabel }}
            </p>
          </div>
          <NextButton
            icon="i-ph-x"
            color="slate"
            variant="ghost"
            size="sm"
            :aria-label="$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.CLOSE')"
            :title="$t('SLA_REPORTS.OPERATIONS.DRILLDOWN.CLOSE')"
            data-testid="sla-drilldown-close"
            @click="closeDrilldown"
          />
        </div>

        <div
          v-if="hasDimensionFilters"
          class="border-b border-n-container px-4 py-3"
        >
          <p class="text-xs font-semibold uppercase text-n-slate-10">
            {{ $t('SLA_REPORTS.OPERATIONS.DRILLDOWN.FILTER_LABEL') }}
          </p>
          <div class="mt-2 flex flex-wrap gap-2">
            <button
              v-for="option in drilldownDimensionOptions"
              :key="option.id"
              type="button"
              class="rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors"
              :class="
                activeDimensionFilter === option.id
                  ? 'border-n-brand bg-n-brand text-white'
                  : 'border-n-container bg-n-solid-2 text-n-slate-11 hover:bg-n-alpha-2'
              "
              :aria-pressed="activeDimensionFilter === option.id"
              :data-testid="`sla-drilldown-filter-${option.id}`"
              @click="activeDimensionFilter = option.id"
            >
              {{ option.label }}
            </button>
          </div>
        </div>

        <div
          v-if="drilldownRecords.length === 0"
          class="px-4 py-8 text-center text-sm text-n-slate-11"
        >
          {{ $t('SLA_REPORTS.OPERATIONS.NO_RECORDS') }}
        </div>

        <div v-else class="divide-y divide-n-container">
          <article
            v-for="record in drilldownRecords"
            :key="record.id"
            class="grid gap-4 px-4 py-4 text-sm text-n-slate-12 xl:grid-cols-[minmax(0,1fr)_minmax(280px,420px)]"
            :data-testid="`sla-drilldown-record-${record.id}`"
          >
            <div class="min-w-0">
              <div class="flex flex-wrap items-center gap-2">
                <span
                  class="rounded-md bg-n-alpha-2 px-2 py-1 text-xs font-medium text-n-slate-11"
                >
                  {{ recordTypeLabel(record) }}
                </span>
                <span
                  class="rounded-md bg-n-blue-2 px-2 py-1 text-xs font-medium text-n-blue-11"
                >
                  {{ stateLabel(record.state) }}
                </span>
              </div>
              <h4 class="mt-3 text-sm font-semibold text-n-slate-12">
                {{ record.title }}
              </h4>

              <dl
                class="mt-3 grid grid-cols-1 gap-x-4 gap-y-2 text-xs md:grid-cols-2"
              >
                <div>
                  <dt class="font-medium text-n-slate-10">
                    {{ $t('SLA_REPORTS.OPERATIONS.COLUMNS.SOURCE') }}
                  </dt>
                  <dd class="mt-1 text-n-slate-12">
                    {{ sourceLabel(record.source) }}
                  </dd>
                </div>
                <div>
                  <dt class="font-medium text-n-slate-10">
                    {{ $t('SLA_REPORTS.OPERATIONS.DRILLDOWN.RULE') }}
                  </dt>
                  <dd class="mt-1 text-n-slate-12">
                    {{ policyLabel(record.policy) }}
                  </dd>
                </div>
                <div>
                  <dt class="font-medium text-n-slate-10">
                    {{ $t('SLA_REPORTS.OPERATIONS.DRILLDOWN.INBOX') }}
                  </dt>
                  <dd class="mt-1 text-n-slate-12">
                    {{ inboxLabel(record.source) }}
                  </dd>
                </div>
                <div>
                  <dt class="font-medium text-n-slate-10">
                    {{ $t('SLA_REPORTS.OPERATIONS.DRILLDOWN.TEAM') }}
                  </dt>
                  <dd class="mt-1 text-n-slate-12">
                    {{ teamLabel(record.source) }}
                  </dd>
                </div>
                <div>
                  <dt class="font-medium text-n-slate-10">
                    {{ $t('SLA_REPORTS.OPERATIONS.DRILLDOWN.CONVERSATION') }}
                  </dt>
                  <dd class="mt-1 text-n-slate-12">
                    {{ conversationLabel(record.source) }}
                  </dd>
                </div>
                <div>
                  <dt class="font-medium text-n-slate-10">
                    {{ $t('SLA_REPORTS.OPERATIONS.DRILLDOWN.CAUSE') }}
                  </dt>
                  <dd class="mt-1 text-n-slate-12">
                    {{ recordCauseLabel(record) }}
                  </dd>
                </div>
                <div>
                  <dt class="font-medium text-n-slate-10">
                    {{ $t('SLA_REPORTS.OPERATIONS.COLUMNS.OWNER') }}
                  </dt>
                  <dd class="mt-1 text-n-slate-12">
                    {{ ownerLabel(record.owner) }}
                  </dd>
                </div>
                <div v-if="record.currentOwner">
                  <dt class="font-medium text-n-slate-10">
                    {{ $t('SLA_REPORTS.OPERATIONS.COLUMNS.CURRENT_OWNER') }}
                  </dt>
                  <dd class="mt-1 text-n-slate-12">
                    {{ currentOwnerLabel(record.currentOwner) }}
                  </dd>
                </div>
              </dl>
            </div>

            <div class="min-w-0 rounded-md bg-n-solid-2 p-3">
              <div
                v-if="
                  pauseHistoryRows(record).length ||
                  pauseDiagnostics(record).length
                "
                class="mb-4 rounded-md border border-n-amber-5 bg-n-amber-2 p-3"
              >
                <h5 class="text-xs font-semibold uppercase text-n-amber-11">
                  {{ $t('SLA_REPORTS.OPERATIONS.DRILLDOWN.PAUSE_HISTORY') }}
                </h5>
                <ol
                  v-if="pauseHistoryRows(record).length"
                  class="mt-3 flex flex-col gap-2"
                >
                  <li
                    v-for="pause in pauseHistoryRows(record)"
                    :key="pause.id"
                    class="rounded-md bg-n-solid-1 px-3 py-2 text-xs text-n-slate-11"
                  >
                    <span class="font-semibold text-n-slate-12">
                      {{ pause.label }}
                    </span>
                    <span class="ml-1">
                      {{
                        $t(
                          'SLA_REPORTS.OPERATIONS.DRILLDOWN.PAUSE_EVENT_META',
                          { time: pause.occurredAt, owner: pause.actor }
                        )
                      }}
                    </span>
                    <span v-if="pause.detail" class="mt-1 block">
                      {{ pause.detail }}
                    </span>
                  </li>
                </ol>
                <ul
                  v-if="pauseDiagnostics(record).length"
                  class="mt-3 flex flex-col gap-1 text-xs text-n-amber-11"
                >
                  <li v-for="line in pauseDiagnostics(record)" :key="line">
                    {{ line }}
                  </li>
                </ul>
              </div>
              <h5 class="text-xs font-semibold uppercase text-n-slate-10">
                {{ $t('SLA_REPORTS.OPERATIONS.DRILLDOWN.TIMELINE') }}
              </h5>
              <div
                v-if="record.timeline.length === 0"
                class="mt-3 text-xs text-n-slate-10"
              >
                {{ $t('SLA_REPORTS.OPERATIONS.NO_RECORDS') }}
              </div>
              <ol v-else class="mt-3 flex flex-col gap-2">
                <li
                  v-for="event in record.timeline"
                  :key="event.id"
                  class="rounded-md border border-n-container bg-n-solid-1 px-3 py-2 text-xs text-n-slate-11"
                >
                  {{ timelineLabel(event) }}
                </li>
              </ol>
            </div>
          </article>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-6 2xl:grid-cols-2">
        <div class="min-w-0">
          <div class="mb-3 flex items-center justify-between gap-3">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ $t('SLA_REPORTS.OPERATIONS.SECTIONS.ACTIVE_BREACHES') }}
            </h3>
            <span class="text-xs font-medium text-n-slate-10">
              {{ activeBreaches.length }}
            </span>
          </div>
          <div class="overflow-x-auto rounded-lg border border-n-container">
            <div class="min-w-[980px]">
              <div
                class="grid grid-cols-12 gap-3 bg-n-slate-2 px-4 py-3 text-xs font-semibold uppercase text-n-slate-11"
              >
                <span class="col-span-1">{{
                  $t('SLA_REPORTS.OPERATIONS.COLUMNS.LEVEL')
                }}</span>
                <span class="col-span-2">{{
                  $t('SLA_REPORTS.OPERATIONS.COLUMNS.MILESTONE')
                }}</span>
                <span class="col-span-1">{{
                  $t('SLA_REPORTS.OPERATIONS.COLUMNS.STATUS')
                }}</span>
                <span class="col-span-2">{{
                  $t('SLA_REPORTS.OPERATIONS.COLUMNS.OWNER')
                }}</span>
                <span class="col-span-2">{{
                  $t('SLA_REPORTS.OPERATIONS.COLUMNS.CURRENT_OWNER')
                }}</span>
                <span class="col-span-2">{{
                  $t('SLA_REPORTS.OPERATIONS.COLUMNS.BREACHED_AT')
                }}</span>
                <span class="col-span-1">{{
                  $t('SLA_REPORTS.OPERATIONS.COLUMNS.NOTIFICATION')
                }}</span>
                <span class="col-span-1">{{
                  $t('SLA_REPORTS.OPERATIONS.COLUMNS.ACTIONS')
                }}</span>
              </div>
              <div
                v-if="activeBreaches.length === 0"
                class="px-4 py-8 text-center text-sm text-n-slate-11"
              >
                {{ $t('SLA_REPORTS.OPERATIONS.NO_RECORDS') }}
              </div>
              <template v-else>
                <div
                  v-for="breach in activeBreaches"
                  :key="breach.id"
                  class="grid grid-cols-12 gap-3 border-t border-n-container px-4 py-3 text-sm text-n-slate-12"
                >
                  <span class="col-span-1 font-semibold">{{
                    breach.level
                  }}</span>
                  <span class="col-span-2 truncate">{{
                    breach.milestone
                  }}</span>
                  <span class="col-span-1 truncate">{{ breach.status }}</span>
                  <span class="col-span-2 truncate">{{
                    ownerLabel(breach.owner)
                  }}</span>
                  <span class="col-span-2 truncate">{{
                    currentOwnerLabel(breach.current_owner)
                  }}</span>
                  <span class="col-span-2 truncate">{{
                    formatDate(breach.breached_at)
                  }}</span>
                  <span class="col-span-1 truncate">{{
                    notificationLabel(breach.notification_delivery)
                  }}</span>
                  <span class="col-span-1 flex items-center gap-1">
                    <NextButton
                      v-if="canAcknowledgeBreaches"
                      icon="i-ph-hand-palm"
                      color="amber"
                      variant="ghost"
                      size="xs"
                      :is-loading="isBreachProcessing(breach)"
                      :disabled="isBreachActionDisabled(breach, 'acknowledge')"
                      :aria-label="
                        $t('SLA_REPORTS.OPERATIONS.ACTIONS.ACKNOWLEDGE')
                      "
                      :title="$t('SLA_REPORTS.OPERATIONS.ACTIONS.ACKNOWLEDGE')"
                      :data-testid="`sla-acknowledge-${breach.id}`"
                      @click="openActionModal('acknowledge', breach)"
                    />
                    <NextButton
                      v-if="canRecoverBreaches"
                      icon="i-ph-check-circle"
                      color="teal"
                      variant="ghost"
                      size="xs"
                      :is-loading="isBreachProcessing(breach)"
                      :disabled="isBreachActionDisabled(breach, 'recover')"
                      :aria-label="$t('SLA_REPORTS.OPERATIONS.ACTIONS.RECOVER')"
                      :title="$t('SLA_REPORTS.OPERATIONS.ACTIONS.RECOVER')"
                      :data-testid="`sla-recover-${breach.id}`"
                      @click="openActionModal('recover', breach)"
                    />
                    <span
                      v-if="!canAcknowledgeBreaches && !canRecoverBreaches"
                      class="text-xs text-n-slate-10"
                    >
                      {{ $t('SLA_REPORTS.OPERATIONS.ACTIONS.NOT_ALLOWED') }}
                    </span>
                  </span>
                </div>
              </template>
            </div>
          </div>
        </div>

        <div class="min-w-0">
          <div class="mb-3 flex items-center justify-between gap-3">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ $t('SLA_REPORTS.OPERATIONS.SECTIONS.TIMERS_AT_RISK') }}
            </h3>
            <span class="text-xs font-medium text-n-slate-10">
              {{ timersAtRisk.length }}
            </span>
          </div>
          <div class="overflow-x-auto rounded-lg border border-n-container">
            <div class="min-w-[1080px]">
              <div
                class="grid grid-cols-12 gap-3 bg-n-slate-2 px-4 py-3 text-xs font-semibold uppercase text-n-slate-11"
              >
                <span class="col-span-2">{{
                  $t('SLA_REPORTS.OPERATIONS.COLUMNS.SOURCE')
                }}</span>
                <span class="col-span-2">{{
                  $t('SLA_REPORTS.OPERATIONS.COLUMNS.MILESTONE')
                }}</span>
                <span class="col-span-1">{{
                  $t('SLA_REPORTS.OPERATIONS.COLUMNS.STATUS')
                }}</span>
                <span class="col-span-2">{{
                  $t('SLA_REPORTS.OPERATIONS.COLUMNS.DUE_AT')
                }}</span>
                <span class="col-span-2">{{
                  $t('SLA_REPORTS.OPERATIONS.COLUMNS.OWNER')
                }}</span>
                <span class="col-span-3">{{
                  $t('SLA_REPORTS.OPERATIONS.COLUMNS.DEADLINE_EXPLANATION')
                }}</span>
              </div>
              <div
                v-if="timersAtRisk.length === 0"
                class="px-4 py-8 text-center text-sm text-n-slate-11"
              >
                {{ $t('SLA_REPORTS.OPERATIONS.NO_RECORDS') }}
              </div>
              <template v-else>
                <div
                  v-for="timer in timersAtRisk"
                  :key="timer.id"
                  class="grid grid-cols-12 gap-3 border-t border-n-container px-4 py-3 text-sm text-n-slate-12"
                >
                  <span class="col-span-2 truncate">{{
                    sourceLabel(timer.source)
                  }}</span>
                  <span class="col-span-2 truncate">{{ timer.milestone }}</span>
                  <span class="col-span-1 truncate">{{ timer.status }}</span>
                  <span class="col-span-2 truncate">{{
                    formatDate(timer.due_at)
                  }}</span>
                  <span class="col-span-2 truncate">{{
                    ownerLabel(timer.owner)
                  }}</span>
                  <span class="col-span-3 min-w-0 text-xs text-n-slate-11">
                    <span class="block font-medium text-n-slate-12">
                      {{ deadlineStatusLabel(timer) }}
                    </span>
                    <span class="mt-1 block">
                      {{ deadlineCalendarLabel(timer) }}
                    </span>
                    <span v-if="deadlineLocalDueLabel(timer)" class="block">
                      {{ deadlineLocalDueLabel(timer) }}
                    </span>
                    <span v-if="deadlinePauseLabel(timer)" class="block">
                      {{ deadlinePauseLabel(timer) }}
                    </span>
                    <span v-if="deadlineHolidayLabel(timer)" class="block">
                      {{ deadlineHolidayLabel(timer) }}
                    </span>
                    <span
                      v-if="deadlineClosedWeekdaysLabel(timer)"
                      class="block"
                    >
                      {{ deadlineClosedWeekdaysLabel(timer) }}
                    </span>
                    <span v-if="deadlineReasonLabel(timer)" class="block">
                      {{ deadlineReasonLabel(timer) }}
                    </span>
                  </span>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div class="mb-3 flex items-center justify-between gap-3">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ $t('SLA_REPORTS.OPERATIONS.SECTIONS.UNSUPPORTED_STEPS') }}
          </h3>
          <span class="text-xs font-medium text-n-slate-10">
            {{ unsupportedSteps.length }}
          </span>
        </div>
        <div class="overflow-x-auto rounded-lg border border-n-container">
          <div class="min-w-[760px]">
            <div
              class="grid grid-cols-12 gap-3 bg-n-slate-2 px-4 py-3 text-xs font-semibold uppercase text-n-slate-11"
            >
              <span class="col-span-2">{{
                $t('SLA_REPORTS.OPERATIONS.COLUMNS.POLICY')
              }}</span>
              <span class="col-span-1">{{
                $t('SLA_REPORTS.OPERATIONS.COLUMNS.LEVEL')
              }}</span>
              <span class="col-span-3">{{
                $t('SLA_REPORTS.OPERATIONS.COLUMNS.NAME')
              }}</span>
              <span class="col-span-2">{{
                $t('SLA_REPORTS.OPERATIONS.COLUMNS.CHANNELS')
              }}</span>
              <span class="col-span-2">{{
                $t('SLA_REPORTS.OPERATIONS.COLUMNS.DELAY')
              }}</span>
              <span class="col-span-2">{{
                $t('SLA_REPORTS.OPERATIONS.COLUMNS.OWNER')
              }}</span>
            </div>
            <div
              v-if="unsupportedSteps.length === 0"
              class="px-4 py-8 text-center text-sm text-n-slate-11"
            >
              {{ $t('SLA_REPORTS.OPERATIONS.NO_RECORDS') }}
            </div>
            <template v-else>
              <div
                v-for="step in unsupportedSteps"
                :key="step.id"
                class="grid grid-cols-12 gap-3 border-t border-n-container px-4 py-3 text-sm text-n-slate-12"
              >
                <span class="col-span-2 truncate">{{
                  idLabel(step.policy_version_id)
                }}</span>
                <span class="col-span-1 font-semibold">{{ step.level }}</span>
                <span class="col-span-3 truncate">{{ step.name }}</span>
                <span class="col-span-2 truncate">{{
                  channelsLabel(step.unsupported_channels)
                }}</span>
                <span class="col-span-2 truncate">{{
                  formatDelay(step.delay_minutes)
                }}</span>
                <span class="col-span-2 truncate">{{
                  ownerLabel(step.owner)
                }}</span>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="isActionModalOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-10 px-4"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="`sla-breach-action-title-${actionModal.breach.id}`"
      data-testid="sla-breach-action-modal"
    >
      <div
        class="w-full max-w-lg rounded-lg border border-n-container bg-n-solid-1 p-5 shadow-xl"
      >
        <div class="flex items-start justify-between gap-4">
          <div class="min-w-0">
            <h3
              :id="`sla-breach-action-title-${actionModal.breach.id}`"
              class="text-base font-semibold text-n-slate-12"
            >
              {{ actionModalTitle }}
            </h3>
            <p class="mt-1 text-sm text-n-slate-11">
              {{
                $t('SLA_REPORTS.OPERATIONS.ACTIONS.BREACH_REFERENCE', {
                  id: actionModal.breach.id,
                })
              }}
            </p>
          </div>
          <NextButton
            icon="i-ph-x"
            color="slate"
            variant="ghost"
            size="sm"
            :aria-label="$t('SLA_REPORTS.OPERATIONS.ACTIONS.CANCEL')"
            :title="$t('SLA_REPORTS.OPERATIONS.ACTIONS.CANCEL')"
            @click="closeActionModal"
          />
        </div>

        <label
          for="sla-breach-action-reason"
          class="mt-5 block text-sm font-medium text-n-slate-12"
        >
          {{ $t('SLA_REPORTS.OPERATIONS.ACTIONS.REASON_LABEL') }}
        </label>
        <textarea
          id="sla-breach-action-reason"
          v-model="actionModal.reason"
          class="mt-2 min-h-28 w-full resize-y rounded-lg border border-n-container bg-n-solid-2 p-3 text-sm text-n-slate-12 outline-none transition-colors placeholder:text-n-slate-9 focus:border-n-brand"
          :placeholder="$t('SLA_REPORTS.OPERATIONS.ACTIONS.REASON_PLACEHOLDER')"
          data-testid="sla-breach-action-reason"
        />
        <p v-if="!isActionReasonValid" class="mt-2 text-xs text-n-slate-10">
          {{ $t('SLA_REPORTS.OPERATIONS.ACTIONS.REASON_REQUIRED') }}
        </p>

        <div class="mt-5 flex justify-end gap-2">
          <NextButton
            color="slate"
            variant="ghost"
            size="sm"
            :label="$t('SLA_REPORTS.OPERATIONS.ACTIONS.CANCEL')"
            @click="closeActionModal"
          />
          <NextButton
            :icon="actionModalIcon"
            color="blue"
            variant="solid"
            size="sm"
            :label="actionModalConfirmLabel"
            :disabled="!isActionReasonValid || isProcessingAction"
            :is-loading="isProcessingAction"
            data-testid="sla-breach-action-submit"
            @click="submitActionModal"
          />
        </div>
      </div>
    </div>
  </section>
</template>
